import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import type { OptionsObject, SnackbarMessage, VariantType } from 'notistack';

export interface Notification extends OptionsObject {
  variant: VariantType;
  children?: JSX.Element;
  dataCy?: string;
  message?: SnackbarMessage;
}

export type Notifications = Notification[];

export const NotificationsActionType = {
  POP_NOTIFICATION: 'POP_NOTIFICATION',
  PUSH_NOTIFICATION: 'PUSH_NOTIFICATION',
} as const;

export type NotificationsActionType =
  (typeof NotificationsActionType)[keyof typeof NotificationsActionType];

export interface PushNotification {
  payload: Notification;
  type: typeof NotificationsActionType.PUSH_NOTIFICATION;
}

interface PopNotification {
  type: typeof NotificationsActionType.POP_NOTIFICATION;
}

export type NotificationsAction = PopNotification | PushNotification;

export const notificationsVar = makeVar<Notifications>([]);

export function pushNotification(notification: Notification): void {
  notificationsVar([...notificationsVar(), notification]);
}

export function popNotification(): Notification | null {
  const [notification, ...remainingNotifications] = notificationsVar();
  notificationsVar(remainingNotifications);
  return notification ?? null;
}

export function dispatchNotificationAction(action: NotificationsAction): void {
  if (action.type === NotificationsActionType.PUSH_NOTIFICATION) {
    pushNotification(action.payload);
    return;
  }

  popNotification();
}

export function useNotificationDispatch() {
  return function dispatch(action: NotificationsAction): void {
    dispatchNotificationAction(action);
  };
}

export function useSelectNotifications(): Notifications {
  return useReactiveVar(notificationsVar);
}
